import { useQueryClient } from '@tanstack/react-query';
import { abortFunnel, changeFunnelStatus, stopAdhocProfileAnalysis } from 'API/linkedin-funnel';
import { ErrorMessages } from 'enums';
import { Funnel, FunnelStatuses } from 'features/co-pilot/co-pilot.types';
import _ from 'lodash';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

interface Params {
  campaign: Funnel | null;
  profileAnalysisProcessing: boolean;
  isProcessing: boolean;
  setCampaign: Dispatch<SetStateAction<Funnel | null>>;
  handleOpenCopilotLicenseExpiredDialog: () => void;
  handleOpenErrorDialog: (error: any, endpoint: string, method: string) => void;
}

const useCampaignStopFlow = ({
  campaign,
  profileAnalysisProcessing,
  isProcessing,
  setCampaign,
  handleOpenCopilotLicenseExpiredDialog,
  handleOpenErrorDialog,
}: Params) => {
  const queryClient = useQueryClient();
  const [stopCampaignLoading, setStopCampaignLoading] = useState(false);
  const [abortLoading, setAbortLoading] = useState(false);
  const [abortRequested, setAbortRequested] = useState(false);

  const handleStopCampaign = async () => {
    if (_.isNil(campaign)) return;

    setStopCampaignLoading(true);
    const res = await changeFunnelStatus(campaign._id, FunnelStatuses.SUSPENDED);

    if (!res.error) {
      setCampaign((prev) => ({ ...prev!, status: FunnelStatuses.SUSPENDED }));
    } else {
      if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
        handleOpenCopilotLicenseExpiredDialog();
      } else {
        handleOpenErrorDialog(res.error, '/campaigns/:id/status', 'post');
      }
    }

    setStopCampaignLoading(false);
  };

  const handleAbortProcessing = async () => {
    if (_.isNil(campaign)) return;

    setAbortLoading(true);
    const res = profileAnalysisProcessing ? await stopAdhocProfileAnalysis(campaign._id) : await abortFunnel(campaign._id);

    if (!res.error) {
      setAbortRequested(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['finalContacts'] }),
      ]);
    } else {
      const request = profileAnalysisProcessing ? `/campaigns/${campaign._id}/adhoc/analyze/stop` : `/campaigns/${campaign._id}/abort`;
      handleOpenErrorDialog(res.error, request, 'post');
      setAbortRequested(false);
    }
    setAbortLoading(false);
  };

  useEffect(() => {
    if (!isProcessing && abortRequested) {
      setAbortRequested(false);
    }
  }, [isProcessing, abortRequested]);

  return {
    stopCampaignLoading,
    abortLoading,
    abortRequested,
    handleStopCampaign,
    handleAbortProcessing,
  };
};

export default useCampaignStopFlow;
